import './init';
import { createRoot } from 'react-dom/client';

import { AppSwitch } from '@app-legacy/AppSwitch';
import { AppProviders } from '@app-legacy/providers/AppProviders';
import { DevTools } from '@shared-legacy/lib/ui/components/DevTools/DevTools';
import { applyCustomTheme } from '@shared-legacy/theme';
import { bootstrapDiscovery, ErrorScreen } from '@app/bootstrap';
import { applyHostTheme, exposeHostThemeDevTools } from '@shared/foundation/theme/applyHostTheme';

import { onUnhandledRequestInDev } from './test/msw.utils';

const root = createRoot(document.getElementById('root')!);
const isDevelopment = import.meta.env.DEV;

/**
 * Renders the error screen when bootstrap fails
 */
const renderError = (error: Error | string | unknown): void => {
  root.render(<ErrorScreen error={error} />);
};

async function enableMocking() {
  const { worker } = await import('./test/browser');
  // `worker.start()` returns a Promise that resolves
  // once the Service Worker is up and ready to intercept requests.
  return worker.start({
    onUnhandledRequest: onUnhandledRequestInDev,
  });
}

/**
 * Bootstraps and renders the application
 */
const loadApp = async (): Promise<void> => {
  try {
    if (import.meta.env.MODE === 'development' && import.meta.env.VITE_USE_MOCK_SERVER === `1`) {
      await enableMocking();
    }
    const { discovery, auth } = await bootstrapDiscovery();

    applyCustomTheme();
    applyHostTheme();
    if (import.meta.env.DEV) {
      exposeHostThemeDevTools();
      (window as Window & { __haicApplyCustomTheme?: () => void }).__haicApplyCustomTheme = applyCustomTheme;
    }

    // TODO: Add React.StrictMode when fluentui team will fix all the errors or submit PRs and do it for them.
    // https://github.com/microsoft/fluentui/issues/4613
    root.render(
      <AppProviders discovery={discovery} auth={auth}>
        <AppSwitch />
        {isDevelopment && <DevTools />}
      </AppProviders>
    );
  } catch (error) {
    renderError(error);
  }
};

loadApp();
